import React, { Fragment } from 'react';
import { GetServerSideProps } from 'next';
import IncomingMessageWithCookies from 'types/IncomingMessageWithCookies';

import { UserContext } from '@zola-helpers/client/dist/es/@types';
import { PrimaryNavIdentifier } from '@zola-helpers/client/dist/es/constants/navConstants';

import { WProductView } from '@zola/svc-web-api-ts-client';

import { fetchNavHTML } from 'libs/server/nav';
import { fetchUser } from 'libs/server/users';
import { useIsPostAuthNav3MobileOrDesktop } from 'hooks/useIsPostAuthNav3Active';

import Nav from 'components/Nav';
import { PageWrapper } from 'components/LP/v3/PageWrapper.styles';
import { DestinationHeader } from 'components/honeymoons/DestinationHeader/DestinationHeader';
import { LANDING_PAGE_DESTINATIONS } from 'constants/honeymoons/honeymoonDestinations';
import HoneymoonDestinationHead from 'components/honeymoons/HoneymoonDestinationHead/HoneymoonDestinationHead';
import { ZOLA_DOMAIN } from 'constants/url';
import { SecondaryNavType } from 'components/Nav/types';
import { getCachedTopicData } from 'libs/contentful/api';
import { SubtopicType } from 'libs/contentful/types';
import { EA_PATH_TOPIC } from 'constants/expertAdvice';
import FeaturedArticlesCarousel from 'components/expert-advice/FeaturedArticlesCarousel';
import {
  CARRY_ON_PRODUCT_IDS,
  FOR_FLIGHT_PRODUCT_IDS,
  TOP_TRAVEL_PRODUCT_IDS,
  TRAVEL_GIFT_CARD_PRODUCT_IDS,
} from 'constants/honeymoons/honeymoonProducts';
import { mapProductsToCategoryTile } from 'components/honeymoons/utils';
import { getProductsByProductIds } from 'libs/server/registry';
import { StyledCategoryTiles } from 'components/honeymoons/HoneymoonDestinationPage.styles';
import Footer from 'components/ui/Footer/Footer';

type HoneymoonsLPProps = {
  navHTML: string | null;
  userCtx: UserContext | null;
  subtopics: SubtopicType[];
  carryOnProducts: WProductView[];
  travelProducts: WProductView[];
  flightProducts: WProductView[];
  giftCardProducts: WProductView[];
};

const HoneymoonsLP: React.FC<HoneymoonsLPProps> = ({
  navHTML,
  userCtx,
  subtopics,
  carryOnProducts,
  travelProducts,
  flightProducts,
  giftCardProducts,
}): JSX.Element | null => {
  const isPostAuthNav3Enabled = useIsPostAuthNav3MobileOrDesktop(userCtx);

  const description = 'Hotels and experiences chosen by experts (and loved by couples!)';
  const title = 'Explore honeymoon destinations';

  return (
    <>
      <HoneymoonDestinationHead
        description={description}
        href={`${ZOLA_DOMAIN}/wedding-planning/honeymoons`}
        title={`${title} - Zola Honeymoon Destinations`}
      />
      <Nav
        {...(isPostAuthNav3Enabled && { activePrimaryLink: PrimaryNavIdentifier.HELP })}
        navHTML={navHTML}
        user={userCtx}
        disablePrimaryNavCollapse
        showPromo
        activePrimaryLink={PrimaryNavIdentifier.HONEYMOONS}
        secondaryNavType={SecondaryNavType.HONEYMOONS}
      />

      <PageWrapper isPostAuthNav3={isPostAuthNav3Enabled}>
        <DestinationHeader
          destinations={LANDING_PAGE_DESTINATIONS}
          title={title}
          description={description}
          showBottomBorder
        />
        <StyledCategoryTiles
          title="Carry-on and luggage sets"
          cta={{
            linkText: 'View all',
            href: '/shop/category/luggage-and-travel',
          }}
          categories={mapProductsToCategoryTile(carryOnProducts)}
          square
        />
        <StyledCategoryTiles
          title="Top travel accessories"
          cta={{
            linkText: 'View all',
            href: '/shop/category/travel-accessories',
          }}
          categories={mapProductsToCategoryTile(travelProducts)}
          square
        />
        <StyledCategoryTiles
          title="Flight essentials"
          categories={mapProductsToCategoryTile(flightProducts)}
          square
        />
        <StyledCategoryTiles
          title="Travel gift cards"
          categories={mapProductsToCategoryTile(giftCardProducts)}
          square
        />
        {subtopics?.map((subtopic, i) =>
          subtopic.featuredArticles && subtopic.featuredArticles.length > 0 ? (
            <Fragment key={i}>
              <FeaturedArticlesCarousel
                articles={subtopic.featuredArticles}
                title={subtopic.name}
                viewAllHref={`${EA_PATH_TOPIC}/honeymoons/${subtopic.slug}`}
              />
            </Fragment>
          ) : (
            <></>
          )
        )}
        <Footer userCtx={userCtx} />
      </PageWrapper>
    </>
  );
};

export const getServerSideProps: GetServerSideProps<HoneymoonsLPProps> = async ({
  req,
}: {
  req: IncomingMessageWithCookies;
}) => {
  const [
    navHTML,
    userCtx,
    topicData,
    carryOnProducts,
    travelProducts,
    flightProducts,
    giftCardProducts,
  ] = await Promise.all([
    fetchNavHTML(req),
    fetchUser(req),
    getCachedTopicData('honeymoons', req),
    getProductsByProductIds(CARRY_ON_PRODUCT_IDS, req),
    getProductsByProductIds(TOP_TRAVEL_PRODUCT_IDS, req),
    getProductsByProductIds(FOR_FLIGHT_PRODUCT_IDS, req),
    getProductsByProductIds(TRAVEL_GIFT_CARD_PRODUCT_IDS, req),
  ]);

  const { topic } = topicData;
  return {
    props: {
      navHTML,
      userCtx,
      subtopics: JSON.parse(JSON.stringify(topic.subtopics)) || [],
      carryOnProducts: carryOnProducts || [],
      travelProducts: travelProducts || [],
      flightProducts: flightProducts || [],
      giftCardProducts: giftCardProducts || [],
    },
  };
};

export default HoneymoonsLP;
